import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

// Utils & Helpers
import { dispatchSnackBarMessage } from "../../../utils/snackbar";
import { getStartAndEndOfDay } from "../../../extras";
import { setIdUrlQueryParam } from "../../../utils/routes";
import { setDateRange } from "../../../utils/filters";

// API
import {
  getShippingRoutes,
  getShippingRoutesHistoryReport,
} from "../../../api/shipping";

// Components
import SearchFilter from "../../search/searchFilter";
import DateFilter from "../../search/dateFilter";
import ShippingRoutesList from "../../shipping/ShippingRoutesList";
import DriverFilter from "../../search/driverFilter";
import ReportTicket from "./ReportTicket";
import PageHeaderLight from "../../ui/PageHeaderLight";

// Material UI
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";

// Icons
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RouteIcon from "@mui/icons-material/Route";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import CloseIcon from "@mui/icons-material/Close";
import TuneIcon from "@mui/icons-material/Tune";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";

// Redux
import { setShowShippingRouteShowModal } from "../../../store/actions/shippingRoutesSlice";

// --- Animation Variants ---

const fadeIn = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 400, damping: 30 },
  },
};

// --- Skeleton Loader ---

const DeliveryListSkeleton = () => (
  <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, overflow: "hidden" }}>
    {[...Array(4)].map((_, i) => (
      <Box key={i} sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
        <Skeleton variant="circular" width={40} height={40} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="55%" height={24} />
          <Skeleton variant="text" width="30%" height={18} />
        </Box>
        <Skeleton variant="rectangular" width={72} height={32} sx={{ borderRadius: 1 }} />
      </Box>
    ))}
  </Paper>
);

// --- Component ---

export default function DeliveriesHistory() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();

  // State
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [shippingRoutes, setShippingRoutes] = useState([]);
  const [showButtonMore, setShowButtonMore] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [reportModalOpen, setReportModalOpen] = useState(false);

  // Modal state
  const [shippingRouteId, setShippingRouteId] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    newSearch: false,
    driver_id: { value: undefined },
    driverName: null,
    dateStart: getStartAndEndOfDay().dateStart,
    dateEnd: getStartAndEndOfDay().dateEnd,
  });

  // Derived: has active driver filter
  const hasDriverFilter = filters.driver_id?.value && filters.driver_id.value !== "ALL";

  // --- Effects ---

  useEffect(() => {
    const initialLoad = async () => {
      try {
        setLoading(true);
        await Promise.all([loadData(filters), loadReportData(filters)]);
        setSubmitted(true);
        handleInitialParam();
      } catch (e) {
        setError("Erro ao carregar dados iniciais. Tente novamente.");
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    initialLoad();
  }, []);

  // --- Handlers ---

  const handleInitialParam = () => {
    const searchParams = new URLSearchParams(location.search);
    const shippingRouteId = searchParams.get("id");
    if (shippingRouteId) handleShowHideShippingRouteShowModal(shippingRouteId);
  };

  const search = async (filtersData) => {
    setLoading(true);
    setError(null);
    setPage(1);

    try {
      const filtersObj = setDateRange(
        filtersData,
        DateFilter.name,
        { useISOFormat: false },
        {
          newSearch: true,
          driverName:
            filtersData[DriverFilter.name]?.label ?? filters.driver_id?.label,
          ...(filtersData[DriverFilter.name]?.value !== "ALL" && {
            driver_id:
              filtersData[DriverFilter.name]?.value ?? filters.driver_id?.value,
          }),
        }
      );

      setFilters(filtersObj);

      await Promise.all([
        loadData(true, filtersData),
        loadReportData(true, filtersData),
      ]);

      setSubmitted(true);
    } catch (e) {
      setError("Erro ao buscar entregas. Tente novamente.");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async (newSearch = true, filtersData = null) => {
    try {
      setRefreshing(true);

      const dateStart =
        filters.dateStart !== "ALL"
          ? filters.dateStart
          : new Date(new Date().setHours(0, 0, 0, 0)).toISOString();

      const dateEnd =
        filters.dateEnd !== "ALL"
          ? filters.dateEnd
          : new Date(new Date().setHours(23, 59, 59, 999)).toISOString();

      const paramPage = newSearch ? 1 : page + 1;
      let params = {
        page: paramPage,
        start_date: dateStart,
        end_date: dateEnd,
      };

      if (filtersData) {
        params = {
          page: 1,
          newSearch: true,
          start_date:
            filtersData[DateFilter.name]?.dateStartString ||
            new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
          end_date:
            filtersData[DateFilter.name]?.dateEndString ||
            new Date(new Date().setHours(23, 59, 59, 999)).toISOString(),
          ...(filtersData[DriverFilter.name]?.value !== "ALL" && {
            driver_id:
              filtersData[DriverFilter.name]?.value ?? filters.driver_id?.value,
          }),
        };
      }

      const {
        data: { routes },
      } = await getShippingRoutes(params);

      setPage(paramPage);
      setShowButtonMore(routes.length > 0);

      const result = newSearch ? routes : [...shippingRoutes, ...routes];
      setShippingRoutes(result);
    } catch (e) {
      console.error("Error loading shipping routes:", e);
      dispatchSnackBarMessage(
        `deliveries-history-${new Date()}`,
        `Erro ao carregar entregas`,
        "error"
      );
    } finally {
      setRefreshing(false);
    }
  };

  const loadReportData = async (newSearch = true, filtersData = null) => {
    try {
      const dateStart =
        filters.dateStart !== "ALL"
          ? filters.dateStart
          : new Date(new Date().setHours(0, 0, 0, 0)).toISOString();

      const dateEnd =
        filters.dateEnd !== "ALL"
          ? filters.dateEnd
          : new Date(new Date().setHours(23, 59, 59, 999)).toISOString();

      const paramPage = newSearch ? 1 : page + 1;
      let params = {
        page: paramPage,
        start_date: dateStart,
        end_date: dateEnd,
      };

      if (filtersData) {
        params = {
          page: 1,
          newSearch: true,
          start_date:
            filtersData[DateFilter.name]?.dateStartString ||
            new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
          end_date:
            filtersData[DateFilter.name]?.dateEndString ||
            new Date(new Date().setHours(23, 59, 59, 999)).toISOString(),
          ...(filtersData[DriverFilter.name]?.value !== "ALL" && {
            driver_id:
              filtersData[DriverFilter.name]?.value ?? filters.driver_id?.value,
          }),
        };
      }

      const { data } = await getShippingRoutesHistoryReport(params);
      setReportData(data);
    } catch (e) {
      console.error("Error loading report data:", e);
      dispatchSnackBarMessage(
        `deliveries-history-${new Date()}`,
        `Erro ao carregar ticket de relatório`,
        "error"
      );
    }
  };

  const handleShippingRouteShowModal = (shippingRouteId) => {
    setIdUrlQueryParam(shippingRouteId, location, history);
    dispatch(
      setShowShippingRouteShowModal({
        show: true,
        id: shippingRouteId,
      })
    );
  };

  const handleShowHideShippingRouteShowModal = (shippingRouteId) => {
    setShippingRouteId(shippingRouteId);
    dispatch(
      setShowShippingRouteShowModal({
        show: false,
        id: null,
      })
    );
  };

  const toggleFilters = () => setFiltersVisible(!filtersVisible);

  // --- Page Header ---

  const pageHeaderActions = [
    {
      key: "relatorio",
      variant: "outlined",
      startIcon: <AssessmentOutlinedIcon />,
      onClick: () => setReportModalOpen(true),
      disabled: !submitted || !reportData,
      children: "Relatório",
    },
    {
      key: "filtros",
      variant: filtersVisible ? "contained" : "outlined",
      startIcon: <FilterListIcon />,
      onClick: toggleFilters,
      children: (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          Filtros
          {hasDriverFilter && !filtersVisible && (
            <Chip
              label="1"
              size="small"
              sx={{
                height: 20,
                minWidth: 20,
                fontSize: "0.7rem",
                fontWeight: 700,
                bgcolor: theme.palette.primary.main,
                color: "white",
              }}
            />
          )}
        </Box>
      ),
    },
  ];

  // --- Empty State ---

  const renderEmptyState = () => (
    <motion.div variants={fadeIn} initial="hidden" animate="visible">
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          {submitted ? (
            <SearchOffIcon
              sx={{ fontSize: 36, color: theme.palette.text.disabled }}
            />
          ) : (
            <RouteIcon
              sx={{ fontSize: 36, color: theme.palette.primary.main }}
            />
          )}
        </Box>

        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}
        >
          {submitted
            ? "Nenhuma entrega encontrada"
            : "Aplique filtros para ver as entregas"}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            maxWidth: 420,
            mb: 4,
            lineHeight: 1.6,
          }}
        >
          {submitted
            ? "Tente ajustar os critérios de busca ou o período selecionado."
            : "Selecione um período e um motorista para visualizar o histórico de entregas e gerar relatórios."}
        </Typography>

        <Button
          variant={submitted ? "outlined" : "contained"}
          startIcon={submitted ? <TuneIcon /> : <SearchIcon />}
          onClick={toggleFilters}
          sx={{ borderRadius: 2, px: 3, py: 1 }}
        >
          {submitted ? "Ajustar Filtros" : "Aplicar Filtros"}
        </Button>
      </Box>
    </motion.div>
  );

  // --- Render ---

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 1800, mx: "auto" }}>
      <PageHeaderLight
        title="Entregas"
        subtitle="Histórico de Entregas"
        actions={pageHeaderActions}
        filters={
          <SearchFilter
            title=""
            filters={[DriverFilter]}
            filterDate
            submit={search}
            initialExpanded
            showHeader={false}
          />
        }
        expandedFilters={filtersVisible}
        toggleFilters={toggleFilters}
      />

      {/* Error Alert */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -8, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Alert
              severity="error"
              variant="standard"
              icon={<ErrorOutlineIcon />}
              action={
                <IconButton
                  aria-label="Fechar alerta de erro"
                  color="inherit"
                  size="small"
                  onClick={() => setError(null)}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
              sx={{
                mb: 3,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {error}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  onClick={() => search()}
                  sx={{ ml: 1, textTransform: "none", fontWeight: 600 }}
                >
                  Tentar novamente
                </Button>
              </Box>
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Loading Skeleton */}
      {loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          <DeliveryListSkeleton />
        </motion.div>
      )}

      {/* Content */}
      {!loading && (
        <>
          {submitted && shippingRoutes.length > 0 ? (
            <motion.div variants={fadeIn} initial="hidden" animate="visible">
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2.5 },
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.background.paper, 0.85),
                  backdropFilter: "blur(12px)",
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: "hidden",
                }}
              >
                {/* Result count header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                    px: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: 500,
                    }}
                  >
                    {shippingRoutes.length} entrega
                    {shippingRoutes.length !== 1 ? "s" : ""}{" "}
                    {showButtonMore ? "carregadas" : "encontradas"}
                  </Typography>
                  {refreshing && (
                    <CircularProgress size={16} sx={{ opacity: 0.6 }} />
                  )}
                </Box>

                <ShippingRoutesList
                  shippingRoutes={shippingRoutes}
                  onShowMore={() => loadData(false)}
                  onViewRoute={handleShippingRouteShowModal}
                  showMore={showButtonMore}
                />
              </Paper>
            </motion.div>
          ) : (
            renderEmptyState()
          )}

          {/* Report Modal */}
          <Dialog
            open={reportModalOpen}
            onClose={() => setReportModalOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                maxHeight: "85vh",
              },
            }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                pb: 1,
              }}
            >
              <Typography variant="h6" fontWeight={600}>
                Relatório
              </Typography>
              <IconButton
                onClick={() => setReportModalOpen(false)}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <ReportTicket
                data={reportData}
                filters={filters}
                driverName={filters.driverName}
              />
            </DialogContent>
          </Dialog>
        </>
      )}
    </Box>
  );
}
